import { z } from 'zod';
import { Block } from '../types';
import { BlockType, zBlockType } from '@strength/domain';

// How many sessions per cycle for block types
export const DEFAULT_CYCLE_LENGTH = 4;
export const CRICKET_BOWLING_CYCLE_LENGTH = 7;

export const DEFAULT_BLOCK_SCHEDULE = [4, 4, 4, 4];
export const CRICKET_BOWLING_BLOCK_SCHEDULE = [7, 7, 7, 7];

export const blockForm = z.object({
  name: z.string().min(3, {
    message: 'Name must be at least 3 characters long',
  }),
  trackId: z.string(),
  blockType: zBlockType,
  startDate: z.string().date(),
  cycleCount: z.coerce.number().int().positive({
    message: 'Cycle count must be positive',
  }),

  sessionCount: z.coerce
    .number()
    .int()
    .min(1, {
      message: 'Session count must equal or greater than 1',
    })
    .max(7, {
      message: 'Session count must be equal or less than 7',
    }),
  blockLayout: z.array(
    z
      .number()
      .int()
      .min(1, { message: 'Session count must equal or greater than 1' })
      .max(7, {
        message: 'Session count must be equal or less than 7',
      }),
  ),

  trainingMaxRatio: z.coerce.number().positive().max(1),

  focuses: z.array(z.object({ id: z.string(), name: z.string() })),
  autoSchedule: z.boolean(),
  allowFlexWorkouts: z.boolean().default(false),
});

export type BlockForm = z.infer<typeof blockForm>;

export const getDefaultValues = (
  block: Partial<Block>,
  isCopy: boolean,
  blockTypes: BlockType[],
): BlockForm => {
  // determine the block schedule format based on the layout
  const blockScheduleFormat =
    getBlockScheduleFormatForBlock(block) ?? DEFAULT_BLOCK_SCHEDULE;

  return {
    name: block.name ? block.name + (isCopy ? ' (Copy)' : '') : '',
    trackId: block.trackId ?? '',
    blockType: block.blockType ?? blockTypes[0] ?? 'strength_training',
    startDate: block.startDate ?? '',
    // TODO: if we allow creating blocks with assymetrical layouts,
    // we should remove the cycle and session count in favor for just using blockLayout
    cycleCount: blockScheduleFormat.length > 0 ? blockScheduleFormat.length : 4,
    sessionCount: blockScheduleFormat[0] ?? DEFAULT_CYCLE_LENGTH,
    blockLayout: blockScheduleFormat,
    trainingMaxRatio: block.trainingMaxRatio ?? 1,
    focuses: block.focuses
      ? block.focuses.map((f) => ({
          id: f.focusId,
          name: f.focus.name,
        }))
      : [],
    autoSchedule:
      block?.autoSchedule === undefined ? false : block.autoSchedule,
    allowFlexWorkouts: block?.allowFlexWorkouts ?? false,
  };
};

const getBlockScheduleFormatForBlock = (block: Partial<Block>): number[] => {
  const { layout, blockType } = block;
  const cycles = Object.keys(layout ?? {});
  const sessions = Object.values(layout ?? {});

  if (cycles.length === 0 || sessions.length === 0) {
    return DEFAULT_BLOCK_SCHEDULE;
  }

  return cycles.map(
    (_, index) =>
      sessions[index] ??
      getDefaultCycleLengthForBlockType(blockType === 'bowling'),
  );
};

export const getBlockScheduleFromCount = (
  cycleCount: number,
  sessionCount: number,
): number[] => {
  return Array(cycleCount).fill(sessionCount);
};

export const getDefaultCycleLengthForBlockType = (isBowlingBlock: boolean) => {
  return isBowlingBlock ? CRICKET_BOWLING_CYCLE_LENGTH : DEFAULT_CYCLE_LENGTH;
};
